import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ProgressIndicator from './ProgressIndicator';
import Breadcrumb from './Breadcrumb';
import ProductSelectionStep from './steps/ProductSelectionStep';
import StyleSelectionStep from './steps/StyleSelectionStep';
import RoofTypeStep from './steps/RoofTypeStep';
import PostcodeStep from './steps/PostcodeStep';
import DimensionsStep from './steps/DimensionsStep';
import ColourStep from './steps/ColourStep';
import HardwareStep from './steps/HardwareStep';
import QuantityStep from './steps/QuantityStep';
import NewRoofTypeStep from './steps/NewRoofTypeStep';
import VerandaDimensionsStep from './steps/VerandaDimensionsStep';
import ConservatoryRoofDimensionsStep from './steps/ConservatoryRoofDimensionsStep';
import SummaryStep from './steps/SummaryStep';
import ThankYouStep from './steps/ThankYouStep';
import { quoteFlowConfig, getProductSteps, getStepConfig } from './flow';
import { getStyleOptionsForProduct } from './styleOptions';

// Validation schemas for different steps
const createValidationSchema = (stepType, formData = {}) => {
  switch (stepType) {
    case 'product-selection':
      return z.object({
        product: z.string().min(1, 'Please select a product'),
      });
    case 'conservatory-style':
    case 'window-style':
    case 'internal-product-style':
    case 'door-style':
    case 'bifolding-door-style':
    case 'veranda-style':
      return z.object({
        style: z.string().min(1, 'Please select a style'),
      });
    case 'conservatory-roof-type':
      return z.object({
        roofType: z.string().min(1, 'Please select a roof type'),
      });
    case 'new-roof-type':
      return z.object({
        newRoofType: z.string().min(1, 'Please select a new roof type'),
      });
    case 'postcode':
      return z.object({
        postcode: z
          .string()
          .min(1, 'Please enter your postcode')
          .regex(/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i, 'Please enter a valid UK postcode')
          .refine(
            async (postcode) => {
              try {
                const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.replace(/\s/g, ''))}`);
                const data = await response.json();
                return data.status === 200 && data.result !== null;
              } catch (error) {
                return false;
              }
            },
            { message: 'This postcode does not exist or is not valid' }
          ),
      });
    case 'dimensions':
      // For verandas-pergolas, quantity is not required
      const dimensionsBase = z.object({
        width: z.string().min(1, 'Please enter width'),
        height: z.string().min(1, 'Please enter height'),
      }).or(z.object({
        dontKnowMeasurements: z.boolean().refine(val => val === true, {
          message: 'Please enter dimensions or select "I don\'t know the measurements"'
        })
      }));

      // Only require quantity if not verandas-pergolas
      if (formData.product === 'verandas-pergolas') {
        return dimensionsBase;
      }

      return z.object({
        quantity: z.string().min(1, 'Please enter quantity'),
      }).and(dimensionsBase);
    case 'veranda-dimensions':
      return z.object({
        width: z.string().min(1, 'Please enter width'),
        height: z.string().min(1, 'Please enter height'),
      });
    case 'conservatory-roof-dimensions':
      return z.object({
        width: z.string().min(1, 'Please enter width'),
        height: z.string().min(1, 'Please enter height'),
      }).or(z.object({
        dontKnowDimensions: z.boolean().refine(val => val === true, {
          message: 'Please enter dimensions or select "I do not know the dimensions required"'
        })
      }));
    case 'colour':
      return z.object({
        colour: z.string().min(1, 'Please select a colour'),
      });
    case 'hardware':
      return z.object({
        hardware: z.string().min(1, 'Please select hardware finish'),
      });
    case 'quantity':
      return z.object({
        quantity: z.string().min(1, 'Please select a quantity'),
      });
    case 'summary':
      return z.object({
        firstName: z.string().min(1, 'Please enter your first name'),
        lastName: z.string().min(1, 'Please enter your last name'),
        email: z
          .string()
          .min(1, 'Please enter your email')
          .email('Please enter a valid email address'),
        phone: z
          .string()
          .min(1, 'Please enter your phone number')
          .regex(
            /^(?:(?:\+44\s?|0)(?:\d\s?){9,10})$/,
            'Please enter a valid UK phone number (e.g., 07123456789 or +44 7123 456789)'
          ),
        postcode: z
          .string()
          .min(1, 'Please enter your postcode')
          .regex(/^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i, 'Please enter a valid UK postcode'),
      });
    default:
      return z.object({});
  }
};

const API_ENDPOINT = 'https://api.clients.icaal.co.uk/forms/form_pk_0Y3iRY9ScMj4mPhzirLH7vv2';

const trackEvent = (eventName, params = {}) => {
  if (typeof gtag === 'function') {
    gtag('event', eventName, params);
  }
};

const formatValue = (value) =>
  value.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const buildOptionsString = (data) => {
  const parts = [];
  if (data.product) parts.push(`Product: ${formatValue(data.product)}`);
  if (data.style) parts.push(`Style: ${formatValue(data.style)}`);
  else if (data.styleUnsure) parts.push('Style: Not sure - needs advice');
  if (data.roofType) parts.push(`Roof Type: ${formatValue(data.roofType)}`);
  if (data.newRoofType) parts.push(`New Roof Type: ${formatValue(data.newRoofType)}`);
  if (data.width && data.height) {
    parts.push(`Dimensions: ${data.width}m x ${data.height}m`);
  } else if (data.dontKnowMeasurements || data.dontKnowDimensions) {
    parts.push('Dimensions: Unknown');
  }
  if (data.quantity) parts.push(`Quantity: ${data.quantity}`);
  if (data.colour) parts.push(`Colour: ${formatValue(data.colour)}`);
  if (data.hardware) parts.push(`Hardware: ${formatValue(data.hardware)}`);
  if (data.glazed) parts.push('Glazed: Yes');
  return parts.join(' | ');
};

const Form = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const prevStepIndexRef = useRef(currentStepIndex);
  // Set when "I'm not sure" skips ahead from a style step, so BACK from the summary
  // returns to that style step instead of the steps that were skipped over.
  const skipOriginIndexRef = useRef(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [formData, setFormData] = useState({
    product: '',
    style: '',
    styleUnsure: false,
    roofType: '',
    newRoofType: '',
    postcode: '',
    width: '',
    height: '',
    quantity: '',
    colour: '',
    hardware: '',
    glazed: false,
    dontKnowDimensions: false,
    dontKnowMeasurements: false,
    hasExistingBase: null,
    newOrExisting: false,
    listedBuilding: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Fire once on mount
  useEffect(() => {
    trackEvent('quote_started', { event_category: 'Quote Form' });
  }, []);

  // Scroll the form into view when the step changes (next/back/breadcrumb) so content isn’t left below the fold.
  // Mobile only: on wider screens a whole step already fits above the fold, so the scroll just moves the page
  // out from under the user. 767.98px is the complement of the `md` breakpoint in tailwind.config.js
  // (min-width: 768px), which is where this form switches to its wide layout.
  useEffect(() => {
    if (prevStepIndexRef.current === currentStepIndex) {
      return;
    }
    prevStepIndexRef.current = currentStepIndex;
    // Measured on every step change rather than once on mount, so a rotate or a window resize is respected.
    if (!window.matchMedia('(max-width: 767.98px)').matches) {
      return;
    }
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [currentStepIndex]);

  // Get the current product's steps dynamically from flow.js
  // Recalculates whenever formData changes to ensure conditional steps are properly included/excluded
  const productSteps = useMemo(() => {
    if (!formData.product) {
      return ['product-selection'];
    }
    const steps = getProductSteps(formData.product, formData);
    console.log('Product Steps recalculated:', {
      product: formData.product,
      style: formData.style,
      steps: steps
    });
    return steps;
  }, [formData]);

  const currentStepId = productSteps[currentStepIndex];
  const currentStepConfig = getStepConfig(currentStepId);

  // Get validation schema for current step
  const validationSchema = createValidationSchema(currentStepId, formData);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: formData,
    mode: 'onSubmit',
  });

  // Define the steps for the progress indicator
  const progressSteps = [
    'Select Your Product',
    'Design Specifications',
    'Submit Your Request',
  ];

  // Calculate which progress step we're on (0, 1, or 2)
  const progressStepIndex = useMemo(() => {
    if (currentStepId === 'product-selection') return 0;
    if (currentStepId === 'summary') return 2;
    return 1; // All other steps are "Design Specifications"
  }, [currentStepId]);

  const handleProductSelect = (productId) => {
    setValue('product', productId);
    // Reset ALL product-specific fields so stale data from a prior flow
    // never bleeds into the new one. We set formData directly (not via
    // spread) to guarantee a clean slate.
    setFormData({
      product: productId,
      style: '',
      styleUnsure: false,
      roofType: '',
      newRoofType: '',
      postcode: '',
      width: '',
      height: '',
      quantity: '',
      colour: '',
      hardware: '',
      glazed: false,
      dontKnowDimensions: false,
      dontKnowMeasurements: false,
      hasExistingBase: null,
      newOrExisting: false,
      listedBuilding: false,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
    trackEvent('quote_product_selected', {
      event_category: 'Quote Form',
      product: productId,
      product_name: quoteFlowConfig.products[productId]?.name,
    });
    clearErrors();
    skipOriginIndexRef.current = null;
    // Advance directly instead of going through handleNext/onSubmit —
    // onSubmit merges { ...formData, ...data } using the closure's stale
    // formData, which would reintroduce the fields we just reset.
    setTimeout(() => {
      setCurrentStepIndex(1);
    }, 400);
  };

  const handleStyleSelect = (styleId) => {
    console.log('Style selected:', styleId);
    setValue('style', styleId);
    setFormData((prev) => ({ ...prev, style: styleId, styleUnsure: false }));
    skipOriginIndexRef.current = null;
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'style',
      value: styleId,
      value_name: formatValue(styleId),
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  // "I'm not sure" on a style step: skip the remaining specification steps and go
  // straight to the summary/contact form. Advances the index directly rather than
  // through handleNext, which would run this step's "please select a style" validation.
  const handleStyleNotSure = () => {
    const updatedFormData = { ...formData, style: '', styleUnsure: true };
    setValue('style', '');
    setFormData(updatedFormData);
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'style',
      value: 'not-sure',
      value_name: 'Not Sure',
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    skipOriginIndexRef.current = currentStepIndex;
    const steps = getProductSteps(updatedFormData.product, updatedFormData);
    setTimeout(() => {
      setCurrentStepIndex(steps.length - 1);
    }, 400);
  };

  const handleRoofTypeSelect = (roofId) => {
    setValue('roofType', roofId);
    setFormData((prev) => ({ ...prev, roofType: roofId }));
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'roof_type',
      value: roofId,
      value_name: formatValue(roofId),
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handleNewRoofTypeSelect = (roofId) => {
    setValue('newRoofType', roofId);
    setFormData((prev) => ({ ...prev, newRoofType: roofId }));
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'new_roof_type',
      value: roofId,
      value_name: formatValue(roofId),
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handleColourSelect = (colourId) => {
    setValue('colour', colourId);
    setFormData((prev) => ({ ...prev, colour: colourId }));
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'colour',
      value: colourId,
      value_name: formatValue(colourId),
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handleHardwareSelect = (hardwareId) => {
    setValue('hardware', hardwareId);
    setFormData((prev) => ({ ...prev, hardware: hardwareId }));
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'hardware',
      value: hardwareId,
      value_name: formatValue(hardwareId),
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handleQuantitySelect = (quantityValue) => {
    setValue('quantity', quantityValue);
    setFormData((prev) => ({ ...prev, quantity: quantityValue }));
    trackEvent('quote_option_selected', {
      event_category: 'Quote Form',
      step: currentStepId,
      option_type: 'quantity',
      value: quantityValue,
      value_name: quantityValue,
      product: formData.product,
      product_name: quoteFlowConfig.products[formData.product]?.name,
    });
    clearErrors();
    // Auto-advance to next step after selection
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handlePostcodeChange = (postcode) => {
    setValue('postcode', postcode);
    setFormData((prev) => ({ ...prev, postcode }));
    clearErrors();
  };

  const handleDimensionChange = (field, value) => {
    setValue(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearErrors();
  };
  const handleQuantityChange = (quantity) => {
    setValue('quantity', quantity);
    setFormData((prev) => ({ ...prev, quantity }));

    clearErrors();

  }

  const handleGlazedChange = (value) => {
    setFormData((prev) => ({ ...prev, glazed: value }));
    clearErrors();
  };

  const handleDontKnowChange = (value) => {
    setValue('dontKnowDimensions', value);
    setFormData((prev) => ({ ...prev, dontKnowDimensions: value }));
    if (value) {
      // Clear dimension fields when "don't know" is checked
      setValue('width', '');
      setValue('height', '');
      setFormData((prev) => ({ ...prev, width: '', height: '' }));
    }
    clearErrors();
  };

  const handleDontKnowMeasurementsChange = (value) => {
    setValue('dontKnowMeasurements', value);
    setFormData((prev) => ({ ...prev, dontKnowMeasurements: value }));
    if (value) {
      // Clear dimension fields when "don't know" is checked
      setValue('width', '');
      setValue('height', '');
      setFormData((prev) => ({ ...prev, width: '', height: '' }));
    }
    clearErrors();
  };

  const handleExistingBaseChange = (value) => {
    setFormData((prev) => ({ ...prev, hasExistingBase: value }));
    clearErrors();
  };

  const handleContactFieldChange = (field, value) => {
    setValue(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearErrors();
  };

  const onSubmit = async (data) => {
    console.log('Step completed:', currentStepId, data);

    // Merge the latest valid data into state so conditional steps always see up-to-date values
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    // Track each step completion
    const stepParams = {
      event_category: 'Quote Form',
      step: currentStepId,
      product: updatedFormData.product,
      product_name: quoteFlowConfig.products[updatedFormData.product]?.name,
    };
    if (currentStepId === 'postcode') {
      trackEvent('quote_postcode_complete', { ...stepParams, postcode: updatedFormData.postcode });
    } else {
      trackEvent('quote_step_complete', stepParams);
    }

    const latestSteps = getProductSteps(updatedFormData.product, updatedFormData);

    // If this is the last step, submit to the API
    if (currentStepIndex >= latestSteps.length - 1) {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const payload = new FormData();
        payload.append('first_name', updatedFormData.firstName);
        payload.append('last_name', updatedFormData.lastName);
        payload.append('email', updatedFormData.email);
        payload.append('phone', updatedFormData.phone);
        payload.append('postcode', updatedFormData.postcode);
        payload.append('options', buildOptionsString(updatedFormData));

        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          body: payload,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.error || 'Submission failed. Please try again.'
          );
        }

        trackEvent('quote_form_complete', {
          event_category: 'Quote Form',
          product: updatedFormData.product,
          product_name: quoteFlowConfig.products[updatedFormData.product]?.name,
          style: updatedFormData.style || undefined,
          postcode: updatedFormData.postcode,
        });
        setFormSubmitted(true);
      } catch (err) {
        console.error('Form submission error:', err);
        setSubmitError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Move to next step (guard against the step list shrinking)
    setCurrentStepIndex((prev) => Math.min(prev + 1, latestSteps.length - 1));
  };

  const handleStartNewQuote = () => {
    // Reset form to initial state
    setFormSubmitted(false);
    setCurrentStepIndex(0);
    setSubmitError(null);
    skipOriginIndexRef.current = null;
    setFormData({
      product: '',
      style: '',
      styleUnsure: false,
      roofType: '',
      newRoofType: '',
      postcode: '',
      width: '',
      height: '',
      quantity: '',
      colour: '',
      hardware: '',
      glazed: false,
      dontKnowDimensions: false,
      dontKnowMeasurements: false,
      hasExistingBase: null,
      newOrExisting: false,
      listedBuilding: false,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
  };

  const handleNext = () => {
    handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    if (currentStepIndex === 0) {
      return;
    }
    if (skipOriginIndexRef.current !== null && currentStepId === 'summary') {
      const originIndex = skipOriginIndexRef.current;
      skipOriginIndexRef.current = null;
      setCurrentStepIndex(originIndex);
      clearErrors();
      return;
    }
    setCurrentStepIndex((prev) => prev - 1);
    clearErrors();
  };

  const handleBreadcrumbClick = (stepIndex) => {
    // Only allow going back to previous steps
    if (stepIndex < currentStepIndex) {
      skipOriginIndexRef.current = null;
      setCurrentStepIndex(stepIndex);
      clearErrors();
    }
  };

  // Render the appropriate step component
  const renderStep = () => {
    const styleOptions = getStyleOptionsForProduct(formData.product);
    const currentStepConfig = getStepConfig(currentStepId);

    switch (currentStepId) {
      case 'product-selection':
        return (
          <ProductSelectionStep
            onSelect={handleProductSelect}
            selectedProduct={formData.product}
          />
        );

      case 'conservatory-style':
      case 'window-style':
      case 'internal-product-style':
      case 'door-style':
      case 'bifolding-door-style':
      case 'veranda-style':
      case 'veranda-pergola-style':
        return (
          <StyleSelectionStep
            onSelect={handleStyleSelect}
            onNotSure={handleStyleNotSure}
            selectedStyle={formData.style}
            options={styleOptions}
            title={currentStepConfig?.title || 'Select Your Style'}
          />
        );

      case 'conservatory-roof-type':
        return (
          <RoofTypeStep
            onSelect={handleRoofTypeSelect}
            selectedRoof={formData.roofType}
          />
        );

      case 'new-roof-type':
        return (
          <NewRoofTypeStep
            onSelect={handleNewRoofTypeSelect}
            selectedRoof={formData.newRoofType}
          />
        );

      case 'colour':
        return (
          <ColourStep
            onSelect={handleColourSelect}
            selectedColour={formData.colour}
            product={formData.product}
            style={formData.style}
          />
        );

      case 'hardware':
        return (
          <HardwareStep
            selectedHardware={formData.hardware}
            onSelect={handleHardwareSelect}
            product={formData.product}
            style={formData.style}
          />
        );

      case 'quantity':
        return (
          <QuantityStep
            selectedQuantity={formData.quantity}
            onSelect={handleQuantitySelect}
            product={formData.product}
            stepConfig={currentStepConfig}
          />
        );

      case 'postcode':
        return (
          <PostcodeStep
            value={formData.postcode}
            onChange={handlePostcodeChange}
            error={errors.postcode?.message}
          />
        );

      case 'dimensions':
        return (
          <DimensionsStep
            width={formData.width}
            height={formData.height}
            quantity={formData.quantity}
            dontKnowMeasurements={formData.dontKnowMeasurements}
            onWidthChange={(val) => handleDimensionChange('width', val)}
            onHeightChange={(val) => handleDimensionChange('height', val)}
            onQuantityChange={(val) => handleQuantityChange(val)}
            onDontKnowMeasurementsChange={handleDontKnowMeasurementsChange}
            errors={{
              width: errors.width?.message,
              height: errors.height?.message,
              quantity: errors.quantity?.message,
            }}
            product={formData.product}
            style={formData.style}
          />
        );

      case 'veranda-dimensions':
        return (
          <VerandaDimensionsStep
            width={formData.width}
            height={formData.height}
            glazed={formData.glazed}
            onWidthChange={(val) => handleDimensionChange('width', val)}
            onHeightChange={(val) => handleDimensionChange('height', val)}
            onGlazedChange={handleGlazedChange}
            errors={{
              width: errors.width?.message,
              height: errors.height?.message,
            }}
            product={formData.product}
            style={formData.style}
          />
        );

      case 'conservatory-roof-dimensions':
        return (
          <ConservatoryRoofDimensionsStep
            width={formData.width}
            height={formData.height}
            dontKnowDimensions={formData.dontKnowDimensions}
            onWidthChange={(val) => handleDimensionChange('width', val)}
            onHeightChange={(val) => handleDimensionChange('height', val)}
            onDontKnowChange={handleDontKnowChange}
            errors={{
              width: errors.width?.message,
              height: errors.height?.message,
            }}
            newRoofType={formData.newRoofType}
          />
        );

      case 'summary':
        return (
          <SummaryStep
            formData={formData}
            firstName={formData.firstName}
            lastName={formData.lastName}
            email={formData.email}
            phone={formData.phone}
            postcode={formData.postcode}
            onFirstNameChange={(val) => handleContactFieldChange('firstName', val)}
            onLastNameChange={(val) => handleContactFieldChange('lastName', val)}
            onEmailChange={(val) => handleContactFieldChange('email', val)}
            onPhoneChange={(val) => handleContactFieldChange('phone', val)}
            onPostcodeChange={handlePostcodeChange}
            errors={{
              firstName: errors.firstName?.message,
              lastName: errors.lastName?.message,
              email: errors.email?.message,
              phone: errors.phone?.message,
              postcode: errors.postcode?.message,
            }}
          />
        );

      default:
        return (
          <div className="text-center">
            <p className="text-gray-500">
              Step "{currentStepId}" component not implemented yet
            </p>
          </div>
        );
    }
  };

  // Show Thank You page after successful submission
  if (formSubmitted) {
    return (
      <div className="relative min-h-screen w-full bg-white py-12">
        <div className="container mx-auto px-4">
          <ThankYouStep onStartNewQuote={handleStartNewQuote} product={formData.product} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full bg-white py-12">
      <div className="container mx-auto px-4">
        {/* Main Title */}
        <h1 className="mb-8 text-center text-[36px] font-bold text-black">
          Start Your Quote
        </h1>

        {currentStepId === 'product-selection' && (
          <p className="-mt-4 mb-8 mx-auto text-center text-[16px] text-[#4c4c4c]">
            Takes 60 seconds &middot; No obligation
          </p>
        )}

        {/* Progress Indicator */}
        <div className="mb-12">
          <ProgressIndicator
            currentStep={progressStepIndex}
            totalSteps={progressSteps.length}
            steps={progressSteps}
          />
        </div>

        {/* Breadcrumb Navigation */}
        <Breadcrumb
          steps={productSteps}
          currentStepIndex={currentStepIndex}
          stepConfigs={quoteFlowConfig.stepConfig}
          onStepClick={handleBreadcrumbClick}
        />

        {/* Form Content — ref target for step-change scroll */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {renderStep()}

          {/* Generic Error Message */}
          {Object.keys(errors).length > 0 && !errors.postcode && (
            <p className="mt-4 text-center text-red-500">
              {Object.values(errors)[0]?.message}
            </p>
          )}

          {/* Submission Error */}
          {submitError && (
            <p className="mt-4 text-center text-red-500">{submitError}</p>
          )}

          {/* Navigation Buttons */}
          <div className="mt-12 flex justify-center gap-4">
            {currentStepIndex > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="rounded-md border border-black bg-white px-8 py-3 text-sm font-normal tracking-wider text-[#252525] transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                BACK
              </button>
            )}

            <button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              className="rounded-md bg-[#ED6B64] px-8 py-3 text-sm font-normal tracking-wider text-white transition-colors hover:bg-[#d85a54] disabled:opacity-50"
            >
              {isSubmitting
                ? 'SUBMITTING...'
                : currentStepIndex === productSteps.length - 1
                ? 'REQUEST YOUR QUOTE'
                : 'NEXT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form;
